import React, { useState, useEffect, useRef } from 'react';
import { User, RefreshCw, LogOut, Lock } from 'lucide-react';
import { PermissionGuard } from '../auth/PermissionGuard';
import { ButtonPermissions } from '../../types/permission';
import usePermissionStore from '../../stores/permissionStore';
import { authService } from '../../services';
import AttendanceModuleView from './AttendanceModuleView';
import AccessReportView from './AccessReportView';
import ChangePasswordDialog from '../dialogs/ChangePasswordDialog';
import RewardManagementView from './RewardManagementView';
import ParameterSettingsView from './ParameterSettingsView';

const ReportViewWithTabs = ({ onRecordClick, onSearch, onTokenAccess, searchParams = {}, searchTrigger = 0 }) => {
  // 用户菜单相关状态
  const { user, role, logout: storeLogout } = usePermissionStore();
  const [loggingOut, setLoggingOut] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const userMenuRef = useRef(null);
  
  // Tab相关状态: 'attendance', 'access', 'reward', 'parameters'
  const [activeTab, setActiveTab] = useState('attendance');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 处理手动刷新
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    setIsRefreshing(true);
    // 动画持续一段时间
    setTimeout(() => setIsRefreshing(false), 800);
  };

  // 处理登出
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await authService.logout();
    } catch (error) {
      console.error('登出错误:', error);
    } finally {
      storeLogout();
      setLoggingOut(false);
      setIsUserMenuOpen(false);
    }
  };

  // 点击外部区域关闭菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      {/* Header */}
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <h1 className="text-xl font-bold text-[#2D3748]">综合</h1>
        <div className="flex items-center gap-3">
          <PermissionGuard permission={ButtonPermissions.reports.export}>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-2 transition-all ${isRefreshing ? 'text-blue-500' : 'text-gray-500 hover:text-blue-600'}`}
            >
              <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
            </button>
          </PermissionGuard>
          
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center"
            >
              <div className="w-8 h-8 rounded-full bg-[#3182CE] flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
            </button>
            
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-[#EDF2F7] rounded-lg shadow-lg z-50">
                <div className="px-4 py-3 border-b border-[#EDF2F7]">
                  <div className="text-sm font-medium text-[#2D3748]">{user?.username || '用户'}</div>
                  <div className="text-xs text-[#718096]">
                    {role === 'admin' ? '管理员' : role === 'teacher' ? '老师' : role === 'student' ? '学生' : role}
                  </div>
                </div>
                <button
                  onClick={() => { setIsChangePasswordOpen(true); setIsUserMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#718096] hover:bg-blue-50 hover:text-[#3182CE] transition-colors border-b border-[#EDF2F7]"
                >
                  <Lock size={16} /> 修改密码
                </button>
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#718096] hover:bg-red-50 hover:text-red-500 transition-colors disabled:opacity-50"
                >
                  <LogOut size={16} /> {loggingOut ? '登出中...' : '登出'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Tab导航 */}
      <div className="flex border-b border-[#EDF2F7] bg-white flex-shrink-0">
        {[
          { id: 'attendance', label: '考勤' },
          { id: 'access', label: '访问' },
          { id: 'reward', label: '赠送' },
          { id: 'parameters', label: '参数' }
        ].map(tab => (
          <button
            key={tab.id}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-24">
        {activeTab === 'attendance' && (
          <AttendanceModuleView 
            searchTrigger={searchTrigger + refreshTrigger} 
            onRecordClick={onRecordClick}
            onTokenAccess={onTokenAccess}
          />
        )}
        {activeTab === 'access' && (
          <AccessReportView searchTrigger={searchTrigger + refreshTrigger} />
        )}
        {activeTab === 'reward' && (
          <RewardManagementView searchTrigger={searchTrigger + refreshTrigger} />
        )}
        {activeTab === 'parameters' && (
          <ParameterSettingsView searchTrigger={searchTrigger + refreshTrigger} />
        )}
      </main>

      <ChangePasswordDialog
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </div>
  );
};

export default ReportViewWithTabs;